import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { FiArrowLeft, FiDownload, FiInfo, FiMail, FiPhone, FiTag, FiUsers, FiCheck, FiExternalLink, FiLink, FiTv, FiCopy, FiMonitor, FiShare2, FiSend } from "react-icons/fi";
import style from "./Monetization.module.css";
import ConfirmDialog from "../Submissions/ConfirmDialog";
import Avatar from "../../component/common/Avatar";
// Reuses the creator-signup form's License terms / Revenue share /
// Restrictions card styling here, read-only, so the admin sees the exact
// same layout the creator filled in — just with every field disabled.
import formStyle from "../CreatorOnboarding/style.module.css";
import { showToast } from "../../utils/Toastify/Toastify";
import { setCreatorTrusted, getCreatorMonetizationAnalytics, exportPayoutsCsv, approveCreator, rejectCreator, requestCreatorCorrection, getCreatorLicensingModels, getAdminAgreementPdfUrl, sendAgreementToCreator, resendSignedAgreement, resendCreatorInvite, impersonateCreator, submitAdminSignature, updateCreatorRevenueShare } from "../../api/HttpApis";
import { useRbac } from "../../rbac/RbacContext";
import ShareInviteModal from "./ShareInviteModal";
import { creatorInviteUrl } from "../../utils/creatorInviteUrl";
import { beginImpersonation, isImpersonating } from "../../utils/impersonation";
import SignaturePad from "../../component/MainLayout/SignaturePad";

// A summary card with an optional ⓘ tooltip (hover to see what the metric
// means) and an optional hint (e.g. "Lifetime") showing what time period the
// value covers — several of these cards are lifetime totals, not this year's.
function StatCard({ label, value, info, hint }) {
  return (
    <div className={style.card}>
      <div className={`${style.cardLabel} ${style.labelWithInfo}`}>
        {label}
        {info && (
          <span title={info} className={style.infoIcon}>
            <FiInfo size={13} />
          </span>
        )}
      </div>
      <div className={style.cardValue}>{value}</div>
      {hint && <div className={style.cardHint}>{hint}</div>}
    </div>
  );
}

const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;
const hrs = (m) => {
  const n = Number(m) || 0;
  const h = Math.floor(n / 60);
  const r = n % 60;
  return h > 0 ? `${h}h ${r}m` : `${r}m`;
};
const body = (res) => res?.data ?? res ?? {};
const CUR_YEAR = new Date().getUTCFullYear();
const TT = { background: "#2a2a2a", border: "1px solid #444", color: "#fff", borderRadius: 8 };
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en", { day: "numeric", month: "short", year: "numeric" }) : "—";
// Ledger/subscription statuses don't map 1:1 to the existing payout chip tones, so
// bucket them onto the closest existing chip color instead of adding new ones.
const statusChip = (s) => {
  const map = {
    paid: "chipPaid",
    earned: "chipApproved",
    booked: "chipApproved",
    active: "chipPaid",
    pending: "chipPending",
    approved: "chipApproved",
    processing: "chipApproved",
    holding: "chipHolding",
    on_hold: "chipOn_hold",
    cancelled: "chipFailed",
    expired: "chipFailed",
    failed: "chipFailed",
    reversed: "chipFailed",
  };
  return style[map[(s || "").toLowerCase()] || "chipPending"];
};
const fromWhom = (l) => {
  if (l.type === "affiliate_commission") return l.from?.subscriber?.name || l.from?.subscriber?.email || "—";
  if (l.type === "payout") return "Creator's commissions above";
  if (l.type === "watch_pool") return "Watch-time pool";
  if (l.type === "tvod") return l.from?.title || "Platform";
  return "Platform";
};

// Ranked content list (Top Content / Content Watch Time) — reuses the
// existing "best performing content" row styling from the Figma design.
/**
 * A thumbnail that collapses to the empty tile when its URL is dead, instead of
 * rendering the browser's broken-image glyph with the title spilling over it.
 */
function BestThumb({ src, alt, className }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) return <div className={className} />;
  return <img className={className} src={src} alt={alt} onError={() => setFailed(true)} />;
}

function ContentList({ title, sub, items }) {
  if (!items?.length) return null;
  return (
    <div className={style.chartCard}>
      <h3 className={style.chartTitle}>{title}</h3>
      <p className={style.chartSub}>{sub}</p>
      {items.map((item, i) => (
        <div className={style.bestRow} key={`${item.video_meta_id}-${i}`}>
          <div className={style.bestRank}>{i + 1}</div>
          {/* Falls back to the empty tile when the thumbnail URL is dead. */}
          <BestThumb src={item.thumbnail} alt={item.title} className={style.bestThumb} />
          <div className={style.bestInfo}>
            <div className={style.bestName}>{item.title}</div>
            <div className={style.bestMeta}>
              {item.type} · {Number(item.view_count || 0).toLocaleString()} views
            </div>
          </div>
          <div className={style.bestEarn}>{hrs(item.watch_minutes)}</div>
        </div>
      ))}
    </div>
  );
}

// Approve/reject/request-correction on a Pending (or Changes Requested)
// creator application. Only rendered for creators the backend will actually
// accept a decision on — the API rejects a decision call on any other status.
const DECISION_ACTIONS = {
  Approved: approveCreator,
  Rejected: rejectCreator,
  "Changes Requested": requestCreatorCorrection,
};

// Where the agreement signature stands, surfaced at the top of the review
// panel so the admin knows whose turn it is before sending a decision —
// in particular that once the creator has signed, GRIEF TV still has to
// countersign. Returns null when there's nothing signature-related to say.
function AgreementSignatureNotice({ agreement, onCountersign }) {
  if (!agreement) return null;
  const { status, sent_at, signed_at, admin_signed_at } = agreement;

  if (status === "SIGNED" && admin_signed_at) {
    return (
      <div className={`${style.agreementNotice} ${style.agreementNoticeDone}`}>
        <FiCheck size={18} className={style.agreementNoticeIcon} />
        <div>
          <div className={style.agreementNoticeTitle}>Agreement executed</div>
          <div className={style.agreementNoticeText}>
            Creator signed on {fmtDate(signed_at)} · GRIEF TV countersigned on {fmtDate(admin_signed_at)}.
          </div>
        </div>
      </div>
    );
  }

  if (status === "SIGNED") {
    return (
      <div className={`${style.agreementNotice} ${style.agreementNoticeAction}`}>
        <FiInfo size={18} className={style.agreementNoticeIcon} />
        <div style={{ flex: 1 }}>
          <div className={style.agreementNoticeTitle}>Creator has signed — your signature is needed</div>
          <div className={style.agreementNoticeText}>
            The creator signed the agreement on {fmtDate(signed_at)}. Countersign it to fully execute the agreement.
          </div>
        </div>
        {onCountersign && (
          <button className={`${style.btn} ${style.btnSm}`} onClick={onCountersign}>
            Sign Document
          </button>
        )}
      </div>
    );
  }

  if (sent_at) {
    return (
      <div className={`${style.agreementNotice} ${style.agreementNoticeWaiting}`}>
        <FiMail size={18} className={style.agreementNoticeIcon} />
        <div>
          <div className={style.agreementNoticeTitle}>Waiting for creator to sign</div>
          <div className={style.agreementNoticeText}>
            Agreement emailed on {fmtDate(sent_at)}{status === "VIEWED" ? " · the creator has opened it" : ""}.
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// The creators list, on the tab/view the admin opened this creator from.
// CreatorRevenue passes its query string (?tab=…&view=…) as route state;
// opened directly (pasted link, new tab) there is none, so it's the plain list.
function useCreatorsListPath() {
  const from = useLocation().state?.from;
  return `/mainlayout/monetization/creators${
    typeof from === "string" && from.startsWith("?") ? from : ""
  }`;
}

// Grant/revoke "publishes without review" for a creator, independent of the
// signup-approval flow. Shown on every creator so existing (already-approved)
// creators can be switched over without re-running their application.
function TrustedCreatorCard({ creator, onChanged }) {
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const trusted = !!creator.is_trusted_creator;

  const apply = async () => {
    setSaving(true);
    try {
      const res = await setCreatorTrusted(creator.id, !trusted);
      showToast(
        res?.data?.message || (trusted ? "Trust removed" : "Creator trusted"),
        "success"
      );
      setConfirming(false);
      onChanged();
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Could not update this setting",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`${style.reviewCard} ${style.mb20}`}>
      <div className={style.trustCardRow}>
        <div className={style.trustCardText}>
          {/* Current state reads before the action does: the heading says what
              is happening right now, the button says what would change. */}
          <div className={style.trustHeadRow}>
            <h3 className={style.reviewTitle}>Content review</h3>
            <span
              className={trusted ? style.trustStateOn : style.trustStateOff}
            >
              <span
                className={trusted ? style.trustDotOn : style.trustDotOff}
              />
              {trusted ? "Publishing without review" : "Review required"}
            </span>
          </div>
          <p className={style.reviewSub}>
            {trusted
              ? "This creator's uploads and edits publish immediately, without waiting in the Submissions queue."
              : "Every upload and edit from this creator goes to the Submissions queue for review."}
          </p>
        </div>
        <button
          className={trusted ? style.trustOffBtn : style.trustOnBtn}
          onClick={() => setConfirming(true)}
          disabled={saving}
        >
          {trusted ? "Require review" : "Publish without review"}
        </button>
      </div>

      {/* This changes how every future upload from this creator is handled, so
          it is never a single stray click. */}
      <ConfirmDialog
        open={confirming}
        tone={trusted ? "danger" : "success"}
        title={trusted ? "Require review again?" : "Publish without review?"}
        message={
          trusted
            ? `${creator.name}'s future uploads and edits will go back to the Submissions queue for approval. Content that is already live stays live until they next edit it.`
            : `${creator.name}'s future uploads and edits will go live immediately, without anyone reviewing them first. Content already in the queue is not affected.`
        }
        confirmLabel={trusted ? "Require review" : "Publish without review"}
        submitting={saving}
        onCancel={() => setConfirming(false)}
        onConfirm={apply}
      />
    </div>
  );
}

function AdministratorReview({ creator, onDecided, onCountersign, canApprove }) {
  const navigate = useNavigate();
  const creatorsListPath = useCreatorsListPath();
  const [decision, setDecision] = useState(canApprove ? "Approved" : "Changes Requested");
  // Approve only appears once the agreement is signed; if it goes away (or
  // shows up) after a refetch, keep the selection on a valid option.
  useEffect(() => {
    setDecision((d) => (!canApprove && d === "Approved" ? "Changes Requested" : d));
  }, [canApprove]);
  const [note, setNote] = useState("");
  // "Publish this creator's content without review" — only meaningful on an
  // Approve decision, so it resets whenever the admin switches away from it.
  const [trustCreator, setTrustCreator] = useState(false);
  useEffect(() => {
    if (decision !== "Approved") setTrustCreator(false);
  }, [decision]);
  const [submitting, setSubmitting] = useState(false);

  const handleSend = async () => {
    if (decision === "Changes Requested" && !note.trim()) {
      showToast("Explain what needs to be corrected before sending.", "error");
      return;
    }
    setSubmitting(true);
    try {
      const action = DECISION_ACTIONS[decision];
      const res =
        decision === "Approved"
          ? await action(creator.id, note.trim() || undefined, trustCreator)
          : await action(creator.id, note.trim() || undefined);
      showToast(res?.data?.message || `Creator ${decision.toLowerCase()}`, "success");
      onDecided();
      // "Changes Requested" keeps the creator in the reviewable list, so this
      // panel would otherwise just re-render in place after a refetch — send
      // the admin back to the list either way, matching the "Back To Creators"
      // button already on this page, instead of leaving them staring at the
      // same form.
      navigate(creatorsListPath);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to send review decision", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`${style.reviewCard} ${style.mb20}`}>
      <div className={style.reviewHeader}>
        <div className={style.reviewHeaderLeft}>
          <div className={style.reviewIconCircle}>
            <FiCheck size={18} />
          </div>
          <div>
            <h3 className={style.reviewTitle}>Administrator review</h3>
            <p className={style.reviewSub}>Update the application status and provide feedback visible to the client.</p>
          </div>
        </div>
        <div>
          <div className={style.reviewProfileLabel}>Client profile</div>
          <div className={style.reviewProfileChip}>{creator.name} · {creator.email}</div>
        </div>
      </div>

      <AgreementSignatureNotice
        agreement={creator.signup_details?.agreement_status}
        onCountersign={onCountersign}
      />

      <div className={style.formGrid}>
        <div className={style.formGroup}>
          <label>Decision / status</label>
          <select className={style.select} value={decision} onChange={(e) => setDecision(e.target.value)}>
            <option value="Approved">Approve agreement</option>
            <option value="Changes Requested">Request correction</option>
            <option value="Rejected">Reject application</option>
          </select>
        </div>
      </div>

      {decision === "Approved" && !creator.is_trusted_creator && (
        <div className={style.trustRow}>
          <label className={style.trustLabel}>
            <input
              type="checkbox"
              checked={trustCreator}
              onChange={(e) => setTrustCreator(e.target.checked)}
            />
            Trust this creator — publish their content without review
          </label>
          <p className={style.trustHelper}>
            Their uploads and edits go live immediately instead of waiting in the
            Submissions queue. Leave unticked to review their content as usual —
            you can still grant this later when approving their first submission.
          </p>
        </div>
      )}

      <div className={style.formGroup} style={{ marginTop: 18 }}>
        <label>Review notes / comments / requests</label>
        <textarea
          className={style.input}
          rows={5}
          style={{ resize: "vertical" }}
          placeholder="Explain the decision, correction request, or proposed revision."
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <span className={style.helper}>Included in the email sent to the creator; not stored on their profile.</span>
      </div>

      <div className={style.formActions}>
        <button className={style.btn} onClick={handleSend} disabled={submitting}>
          {submitting ? "Sending..." : "Send review decision"}
        </button>
      </div>
    </div>
  );
}

export default function CreatorDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const creatorsListPath = useCreatorsListPath();
  const { can } = useRbac();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // A banner URL that 404s would otherwise leave an empty grey frame on the page.
  const [bannerFailed, setBannerFailed] = useState(false);
  const [ledgerMonth, setLedgerMonth] = useState(null);
  const [showAdminSignaturePad, setShowAdminSignaturePad] = useState(false);
  const [sharingInvite, setSharingInvite] = useState(false);
  const [resendingInvite, setResendingInvite] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [countersigning, setCountersigning] = useState(false);

  // Shared with fetchCreator below so a post-decision refetch (triggered by
  // AdministratorReview, which can outlive this page if the admin navigates
  // back right after deciding) also skips setState once unmounted.
  const activeRef = useRef(true);

  useEffect(() => {
    activeRef.current = true;
    setLoading(true);
    (async () => {
      try {
        const res = await getCreatorMonetizationAnalytics(userId, { year: CUR_YEAR });
        if (activeRef.current) setData(body(res));
      } catch (e) {
        if (activeRef.current) showToast(e?.response?.data?.message || "Could not load creator.", "error");
      } finally {
        if (activeRef.current) setLoading(false);
      }
    })();
    return () => { activeRef.current = false; };
  }, [userId]);


  // Re-fetch (no loading flash) after an approve/reject decision so the
  // review card disappears once the creator's status is no longer Pending.
  const fetchCreator = async () => {
    try {
      const res = await getCreatorMonetizationAnalytics(userId, { year: CUR_YEAR });
      if (activeRef.current) setData(body(res));
    } catch (e) {
      if (activeRef.current) showToast(e?.response?.data?.message || "Could not load creator.", "error");
    }
  };

  if (loading) return <div className={style.page}><div className={style.loading}>Loading…</div></div>;
  if (!data?.creator) return <div className={style.page}><div className={style.empty}>Creator not found.</div></div>;

  const c = data.creator;

  // Invite not accepted yet — same gate as the creator list's share button.
  const canShareInvite = c.status === "Invited" && !!c.email && can("users", "create");
  const inviteUrl = canShareInvite ? creatorInviteUrl(c.email) : null;
  // Only a real admin session (not one already inside a creator portal).
  const canOpenPortal =
    c.status === "Approved" &&
    can("users", "impersonate") &&
    localStorage.getItem("role") === "Admin" &&
    !isImpersonating();

  const handleOpenPortal = async () => {
    if (
      !window.confirm(
        `Open ${c.name}'s creator portal?\n\nYou'll act as ${c.email} for up to 1 hour. Everything you change is recorded. Use "Return to admin" to come back.`
      )
    ) return;
    setOpeningPortal(true);
    try {
      const res = await impersonateCreator(userId);
      beginImpersonation(res.data.data, `/mainlayout/monetization/creators/${userId}`);
    } catch (error) {
      showToast(error?.response?.data?.message || "Could not open the creator portal", "error");
      setOpeningPortal(false);
    }
  };

  const handleResendInvite = async () => {
    setResendingInvite(true);
    try {
      const res = await resendCreatorInvite(userId);
      showToast(res?.message || res?.data?.message || "Invitation email resent", "success");
    } catch (error) {
      showToast(error?.response?.data?.message || "Could not resend the invitation email", "error");
    } finally {
      setResendingInvite(false);
    }
  };
  // The web app doesn't expose a channel-preview URL of its own, so derive it
  // from the referral link's host (same site, different path) rather than
  // hardcoding an environment-specific origin.
  let channelPreviewLink = null;
  if (c.referral_link) {
    try {
      channelPreviewLink = `${new URL(c.referral_link).origin}/creator-channel/${userId}`;
    } catch {
      channelPreviewLink = null;
    }
  }
  const cards = data.cards || {};
  const watchTrend = (data.watch_trend || []).map((w) => ({ name: w.label, hours: +(w.minutes / 60).toFixed(2) }));
  const revTrend = (data.revenue_trend || []).map((t) => ({
    name: t.period?.slice(5) || t.period,
    affiliate: (t.affiliate_cents || 0) / 100,
    watch: (t.watch_pool_cents || 0) / 100,
    tvod: (t.tvod_cents || 0) / 100,
  }));

  const handleDownload = async () => {
    try {
      await exportPayoutsCsv({ year: CUR_YEAR });
      showToast("Report download started", "success");
    } catch {
      showToast("Download failed", "error");
    }
  };

  const handleCopyLink = async (link, successMessage) => {
    try {
      await navigator.clipboard.writeText(link);
      showToast(successMessage, "success");
    } catch {
      showToast("Could not copy link.", "error");
    }
  };

  const handleCountersign = async ({ dataUrl, file }) => {
    setCountersigning(true);
    try {
      const formData = new FormData();
      if (file) {
        formData.append("signature_image", file);
      } else {
        formData.append("signature_data_url", dataUrl);
      }
      await submitAdminSignature(userId, formData);
      showToast("Countersignature saved", "success");
      setShowAdminSignaturePad(false);
      fetchCreator();
    } catch (error) {
      showToast(error?.response?.data?.message || "Couldn't save countersignature", "error");
    } finally {
      setCountersigning(false);
    }
  };

  return (
    <div className={style.page}>
      <div className={style.headerRow}>
        <button className={`${style.btn} ${style.btnGhost}`} onClick={() => navigate(creatorsListPath)}>
          <FiArrowLeft className={style.iconMr6} /> Back To Creators
        </button>
        {canOpenPortal && (
          <button className={style.btn} onClick={handleOpenPortal} disabled={openingPortal}>
            <FiMonitor className={style.iconMr6} />
            {openingPortal ? "Opening…" : "Open creator portal"}
          </button>
        )}
        <button className={`${style.btn} ${style.btnGhost}`} onClick={handleDownload}>
          <FiDownload className={style.iconMr6} /> Download Report
        </button>
      </div>

      {/* Invite not accepted yet: show the signup link so an admin can resend
          or share it directly when the email didn't arrive. */}
      {canShareInvite && (
        <div className={style.cpInvite}>
          <div className={style.cpInviteHead}>
            <span className={style.cpInviteIcon}>
              <FiSend size={16} />
            </span>
            <div>
              <div className={style.cpInviteTitle}>Invitation pending</div>
              <div className={style.cpInviteText}>
                {c.full_name || c.name} hasn&apos;t accepted the invite yet. If the email didn&apos;t
                arrive, resend it or share the signup link directly.
              </div>
            </div>
          </div>
          <div className={style.cpInviteLink} title={inviteUrl}>
            <FiLink size={13} />
            <span>{inviteUrl}</span>
          </div>
          <div className={style.cpInviteActions}>
            <button
              type="button"
              className={style.cpBtnPrimary}
              onClick={handleResendInvite}
              disabled={resendingInvite}
            >
              {resendingInvite ? <span className={style.cpSpinner} /> : <FiMail size={14} />}
              {resendingInvite ? "Sending…" : "Resend email"}
            </button>
            <button type="button" className={style.cpBtnGhost} onClick={() => setSharingInvite(true)}>
              <FiShare2 size={14} /> Share link
            </button>
            <button
              type="button"
              className={style.cpBtnGhost}
              onClick={() => handleCopyLink(inviteUrl, "Invite link copied!")}
            >
              <FiCopy size={14} /> Copy
            </button>
          </div>
        </div>
      )}

      {/* Channel banner (if the creator uploaded one at signup). */}
      {/* Banner has no initial to fall back to, so a dead URL hides the whole
          strip rather than leaving an empty grey frame with alt text in it. */}
      {c.channel?.banner && !bannerFailed && (
        <div className={style.creatorBanner}>
          <img
            src={c.channel.banner}
            alt={`${c.name} banner`}
            onError={() => setBannerFailed(true)}
          />
        </div>
      )}

      <div className={style.profileCard}>
        {/* A dead URL is not the same as no URL: Avatar only reveals the image
            once the browser confirms it loaded, so a 404 falls back to the
            creator's initial instead of a broken box with spilling alt text. */}
        <Avatar
          src={c.profile_img}
          name={c.name}
          className={style.profileAvatar}
        />
        <div className={style.profileInfo}>
          <h2 className={style.profileName}>{c.name}</h2>
          {/* Show the person's real name when it differs from the channel name. */}
          {c.full_name && c.full_name !== c.name && (
            <div className={style.profileSub}>{c.full_name}</div>
          )}
          <div className={style.profileMeta}>
            {c.email && (
              <span className={style.metaItem} title={c.email}>
                <FiMail size={13} /> <span className={style.metaEllipsis}>{c.email}</span>
              </span>
            )}
            {c.phone && (
              <span className={style.metaItem}>
                <FiPhone size={13} /> {c.phone}
              </span>
            )}
            {c.channel?.category && (
              <span className={style.metaItem}>
                <FiTag size={13} /> {c.channel.category}
              </span>
            )}
            {c.channel?.subscribers > 0 && (
              <span className={style.metaItem}>
                <FiUsers size={13} /> {Number(c.channel.subscribers).toLocaleString()} subscribers
              </span>
            )}
          </div>
          {(c.referral_link || channelPreviewLink) && (
            <div className={style.profileLinks}>
              {c.referral_link && (
                <div className={style.profileLinkRow}>
                  <span className={style.profileLinkLabel}>
                    <FiLink size={12} /> Referral link
                  </span>
                  <a
                    className={style.profileLink}
                    href={c.referral_link}
                    target="_blank"
                    rel="noreferrer"
                    title={c.referral_link}
                  >
                    {c.referral_link}
                  </a>
                  <button
                    type="button"
                    className={style.linkCopyBtn}
                    title="Copy referral link"
                    onClick={() => handleCopyLink(c.referral_link, "Referral link copied!")}
                  >
                    <FiCopy size={13} />
                  </button>
                </div>
              )}
              {channelPreviewLink && (
                <div className={style.profileLinkRow}>
                  <span className={style.profileLinkLabel}>
                    <FiTv size={12} /> Channel preview
                  </span>
                  <a
                    className={style.profileLink}
                    href={channelPreviewLink}
                    target="_blank"
                    rel="noreferrer"
                    title={channelPreviewLink}
                  >
                    {channelPreviewLink}
                  </a>
                  <button
                    type="button"
                    className={style.linkCopyBtn}
                    title="Copy channel preview link"
                    onClick={() => handleCopyLink(channelPreviewLink, "Channel preview link copied!")}
                  >
                    <FiCopy size={13} />
                  </button>
                </div>
              )}
            </div>
          )}
          {c.joined_at && (
            <span className={style.joinBadge}>
              Joined {new Date(c.joined_at).toLocaleDateString("en", { day: "numeric", month: "short", year: "numeric" })}
            </span>
          )}
        </div>
      </div>

      {/* Content review setting. Rendered for every creator at any status —
          the checkbox on the approval panel below is only reachable while an
          application is still Pending, so this is the only way to grant (or
          revoke) trust for a creator who was approved earlier. */}
      {can("users", "edit") && (
        <TrustedCreatorCard creator={c} onChanged={fetchCreator} />
      )}

      {["Pending", "Changes Requested"].includes(c.status) && can("users", "edit") && (
        <AdministratorReview
          creator={c}
          onDecided={fetchCreator}
          onCountersign={() => setShowAdminSignaturePad(true)}
        />
      )}

      {/* Channel description + bio captured at creator signup. */}
      {(c.channel?.description || c.bio) && (
        <div className={style.detailCard}>
          {c.channel?.description && (
            <div className={style.detailBlock}>
              <div className={style.detailLabel}>Channel Description</div>
              <p className={style.detailText}>{c.channel.description}</p>
            </div>
          )}
          {c.bio && (
            <div className={style.detailBlock}>
              <div className={style.detailLabel}>Bio</div>
              <p className={style.detailText}>{c.bio}</p>
            </div>
          )}
        </div>
      )}

      {/* Supporting documents uploaded at signup. */}
      {c.documents?.length > 0 && (
        <div className={`${formStyle.licenseCard} ${style.licenseCardGap}`}>
          <div className={formStyle.licenseHeader}>
            <div className={formStyle.licenseStep}>01</div>
            <div>
              <h3 className={formStyle.licenseTitle}>Documents</h3>
              <p className={formStyle.licenseSub}>Supporting records uploaded with this application.</p>
            </div>
          </div>

          <div className={style.docList}>
            {c.documents.map((doc) => {
              const label = doc.original_filename || doc.file_url || "";
              const ext = label.includes(".") ? label.split(".").pop().slice(0, 4).toUpperCase() : "FILE";
              return (
                <a
                  key={doc.id}
                  href={doc.file_url}
                  target="_blank"
                  rel="noreferrer"
                  className={style.docRow}
                >
                  <div className={style.docIcon}>{ext}</div>
                  <div className={style.docInfo}>
                    <span className={style.docName}>{label}</span>
                    <span className={style.docMeta}>
                      {doc.category}
                      {doc.related_title ? ` · ${doc.related_title}` : ""}
                    </span>
                  </div>
                  <FiExternalLink className={style.docOpenIcon} />
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Analytics/revenue data is meaningless before the creator is
          Approved (no content, no traffic yet) and would just show a wall
          of misleading $0.00s — hide the whole block until then. */}
      {c.status === "Approved" && (
        <>
      <div className={style.cardGrid}>
        <StatCard
          label="Total Content"
          value={cards.total_content ?? 0}
          info="Number of videos this creator has uploaded."
        />
        <StatCard
          label="Referral Clicks"
          value={cards.referral_clicks ?? 0}
          info="How many times this creator's referral link was opened."
        />
        <StatCard
          label="Referral Signups"
          value={cards.referral_signups ?? 0}
          info="People who created an account through this creator's link."
        />
        <StatCard
          label="Conversions"
          value={cards.referral_conversions ?? 0}
          info="Referred users who bought a paid plan (the creator earns commission on these)."
        />
        <StatCard
          label="Total Referrals"
          value={cards.total_referrals ?? 0}
          info="All users ever referred by this creator (paid or not)."
        />
        <StatCard
          label="Total Subscribers"
          value={cards.total_subscribers ?? 0}
          info="Referred users who currently have an active paid subscription."
        />
        <StatCard
          label="Subscribers Revenue"
          value={money(cards.subscribers_revenue_cents)}
          info="All-time affiliate commission earned from this creator's referred subscribers, not just this year."
          hint="Lifetime"
        />
        <StatCard
          label="Total Watch Time"
          value={hrs(cards.total_watch_minutes)}
          info="All-time total of time people spent watching this creator's content, not just this year."
          hint="Lifetime"
        />
        <StatCard
          label="Watch Time Revenue"
          value={money(cards.watch_time_revenue_cents)}
          info="This creator's all-time share of the watch-time pool, based on minutes watched — not just this year."
          hint="Lifetime"
        />
        <StatCard
          label="TVOD Revenue"
          value={money(cards.tvod_revenue_cents)}
          info="All-time earnings from buy/rent (TVOD) sales of this creator's content, not just this year."
          hint="Lifetime"
        />
      </div>

      <div className={style.chartsRow}>
        <div className={style.chartCard}>
          <h3 className={style.chartTitle}>Platform Performance</h3>
          <p className={style.chartSub}>Hours people watched this creator's content ({CUR_YEAR})</p>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={watchTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="name" stroke="#fff" />
              <YAxis stroke="#fff" />
              <Tooltip contentStyle={TT} formatter={(v) => `${v} hrs`} />
              <Line type="monotone" dataKey="hours" stroke="#b87333" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className={style.chartCard}>
          <h3 className={style.chartTitle}>Last 6 Months Revenue</h3>
          <p className={style.chartSub}>Affiliate vs watch-pool vs TVOD earnings</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={revTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="name" stroke="#fff" />
              <YAxis stroke="#fff" />
              <Tooltip contentStyle={TT} formatter={(v) => `$${Number(v).toFixed(2)}`} />
              <Bar dataKey="affiliate" fill="#b87333" name="Affiliate" radius={[4, 4, 0, 0]} />
              <Bar dataKey="watch" fill="#e0b07a" name="Watch Pool" radius={[4, 4, 0, 0]} />
              <Bar dataKey="tvod" fill="#6f9ceb" name="TVOD" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className={style.chartsRow}>
        <ContentList
          title="Top Content"
          sub="Best performing content by views"
          items={data.top_content}
        />
        <ContentList
          title="Content Watch Time"
          sub="Breakdown of watch minutes by title"
          items={data.content_watch_time}
        />
      </div>

      {(() => {
        const byMonth = data.ledger?.by_month || [];
        const currentMonthNum = data.ledger?.current_month?.month ?? null;
        const activeMonthNum = ledgerMonth ?? currentMonthNum ?? byMonth[0]?.month ?? null;
        const activeMonth = byMonth.find((m) => m.month === activeMonthNum) || null;
        const ledgerEntries = activeMonth?.entries || [];
        if (!byMonth.length) return null;
        return (
          <div className={`${style.tableWrap} ${style.mb20}`}>
            <div className={style.tableHeaderRow}>
              <div>
                <h3 className={`${style.chartTitle} ${style.chartTitleTight}`}>Transaction History</h3>
                <p className={style.chartSub}>Payouts and earnings ledger, by month</p>
              </div>
              <select
                className={`${style.select} ${style.selectAuto}`}
                value={activeMonthNum ?? ""}
                onChange={(e) => setLedgerMonth(Number(e.target.value))}
              >
                {byMonth.map((m) => (
                  <option key={m.month} value={m.month}>
                    {m.label}
                    {m.month === currentMonthNum ? " (Current)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className={style.tableScroll}>
              <table className={style.table}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>From</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerEntries.length === 0 ? (
                    <tr><td colSpan={6} className={style.empty}>No ledger entries for {activeMonth?.label || "this month"}.</td></tr>
                  ) : (
                    ledgerEntries.map((l, i) => (
                      <tr key={i}>
                        <td>{fmtDate(l.date)}</td>
                        <td className={style.capitalize}>{(l.type || "").replace(/_/g, " ")}</td>
                        <td>{fromWhom(l)}</td>
                        <td>
                          {l.description}
                          {l.from?.line_items?.length > 0 && (
                            <div className={style.lineItems}>
                              {l.from.line_items.map((li, j) => (
                                <div key={j}>{li.description}: {money(li.amount_cents)}</div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td>{money(l.amount_cents)}</td>
                        <td>
                          <span className={`${style.chip} ${statusChip(l.status)}`}>{l.status}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {data.payout_status?.accumulated_cents > 0 && (() => {
        const ps = data.payout_status;
        const blockedText =
          ps.blocked_by === "hold_period"
            ? `waiting out the ${ps.hold_period_days}-day hold`
            : ps.blocked_by === "below_minimum"
              ? `${money(ps.shortfall_cents)} short of the ${money(ps.threshold_cents)} minimum`
              : "ready to release on the next payout run";
        return (
          <div className={style.tableWrap}>
            <div className={style.sectionHeaderPad}>
              <h3 className={`${style.chartTitle} ${style.chartTitleTight}`}>Held / Accumulating</h3>
              <p className={style.chartSub}>
                {money(ps.accumulated_cents)} earned since {ps.oldest_unpaid_period}, not yet paid —{" "}
                {blockedText}
                {ps.hold_release_at
                  ? ` (releases ${new Date(ps.hold_release_at).toLocaleDateString()})`
                  : ""}
                .
              </p>
            </div>
            <div className={style.tableScroll}>
              <table className={style.table}>
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Watch Pool</th>
                    <th>Affiliate</th>
                    <th>TVOD</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {ps.accumulating_periods.map((row) => (
                    <tr key={row.period}>
                      <td>{row.period}</td>
                      <td>{money(row.watch_pool_cents)}</td>
                      <td>{money(row.affiliate_cents)}</td>
                      <td>{money(row.tvod_cents)}</td>
                      <td>{money(row.total_cents)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ fontWeight: 600 }}>Total</td>
                    <td colSpan={3}></td>
                    <td style={{ fontWeight: 600 }}>{money(ps.accumulated_cents)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {data.subscribers?.length > 0 && (
        <div className={style.tableWrap}>
          <div className={style.sectionHeaderPad}>
            <h3 className={`${style.chartTitle} ${style.chartTitleTight}`}>Subscribers</h3>
            <p className={style.chartSub}>Referred users with active subscriptions</p>
          </div>
          <div className={style.tableScroll}>
            <table className={style.table}>
              <thead>
                <tr>
                  <th>Subscriber</th>
                  <th>Plan</th>
                  <th>Amount</th>
                  <th>Billing</th>
                  <th>Platform</th>
                  <th>Purchased</th>
                  <th>Expires</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.subscribers.map((s, i) => (
                  <tr key={i}>
                    <td>
                      <div className={style.subscriberRow}>
                        <Avatar
                          src={s.subscriber?.profile_img}
                          name={s.subscriber?.name}
                          className={style.subAvatarImg}
                        />
                        <div>
                          <div>{s.subscriber?.name}</div>
                          <div className={style.subEmail}>{s.subscriber?.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>{s.plan?.name}</td>
                    <td>{s.currency} {Number(s.amount).toFixed(2)}</td>
                    <td className={style.capitalize}>{s.plan_price?.billing_cycle}</td>
                    <td className={style.capitalize}>{s.platform}</td>
                    <td>{fmtDate(s.purchase_date)}</td>
                    <td>{fmtDate(s.expiration_date)}</td>
                    <td>
                      <span className={`${style.chip} ${statusChip(s.status)}`}>{s.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
        </>
      )}

      {showAdminSignaturePad && (
        <SignaturePad
          onSubmit={handleCountersign}
          onClose={() => setShowAdminSignaturePad(false)}
          submitting={countersigning}
        />
      )}
      {sharingInvite && canShareInvite && (
        <ShareInviteModal creator={c} onClose={() => setSharingInvite(false)} />
      )}
    </div>
  );
}
